import { lazy, Suspense, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Typography } from '@mui/material';

import { useMatchingSession } from '../hooks/useMatchingSession';
import { MatchingResultPayload, MatchingSortMode } from '../types';
import { buildResultsView } from '../utils/build-results-view';
import { buildPairKey } from '../session';
import { GcSection } from './GcSection';

// Lazy: the modal's TipTap editor drags in `@tiptap/pm` (ships unbuilt `.ts` — breaks Jest's
// babel transform for ANY eager importer, testing.md), and there's no reason to ship it in the
// initial results-screen chunk before the user ever opens the composer.
const ComposeMatchEmailModal = lazy(() =>
  import('./ComposeMatchEmailModal').then(module => ({ default: module.ComposeMatchEmailModal })),
);

interface MatchingResultsScreenProps {
  readonly result: MatchingResultPayload | null | undefined;
  readonly runId?: string | null;
  readonly onRunAgain: () => void;
}

interface ActivePair {
  readonly applicationId: number;
  readonly questionnaireId: number;
}

// DOM order kept as drawn (Score first) even though the *default active* mode is date (G-3).
const SORT_MODES: { mode: MatchingSortMode; labelKey: string }[] = [
  { mode: MatchingSortMode.score, labelKey: 'matching.results.sortByScore' },
  { mode: MatchingSortMode.date, labelKey: 'matching.results.sortByDate' },
];

export const MatchingResultsScreen = ({ result, runId, onRunAgain }: MatchingResultsScreenProps) => {
  const { t } = useTranslation('common');
  const [sortMode, setSortMode] = useState<MatchingSortMode>(MatchingSortMode.date);
  const [activePair, setActivePair] = useState<ActivePair | null>(null);
  const { sentPairKeys, markPairSent } = useMatchingSession();

  const gcs = useMemo(() => buildResultsView(result, sortMode), [result, sortMode]);

  // A malformed blob and a legitimately EMPTY GC pool both yield zero sections — only the first is
  // an error. 02 guards the IP pool, never the GC pool, so `gcs: []` on a completed run is reachable.
  if (!Array.isArray(result?.gcs)) {
    return (
      <Typography variant="body1" color="text.secondary">
        {t('matching.results.unavailable')}
      </Typography>
    );
  }

  return (
    // SPEC §4.9: rows are never removed and nothing groups/pins by `proposed` — the send ticket only decorates.
    <Box>
      <Typography variant="h1" sx={{ mb: 3 }}>
        {t('matching.page.title')}
      </Typography>

      <Box
        sx={{
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'grey.500',
          borderRadius: 5,
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h2">{t('matching.results.title')}</Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="h5" color="text.secondary">
                {t('matching.results.sortBy')}
              </Typography>

              <Box sx={{ display: 'flex', gap: 1 }}>
                {SORT_MODES.map(({ mode, labelKey }) => {
                  const active = sortMode === mode;

                  return (
                    <Box
                      key={mode}
                      component="button"
                      type="button"
                      aria-pressed={active}
                      onClick={() => setSortMode(mode)}
                      sx={{
                        height: 32,
                        px: 2,
                        borderRadius: 2,
                        fontSize: 14,
                        lineHeight: '1.4',
                        fontFamily: 'inherit',
                        boxSizing: 'border-box',
                        border: 1,
                        cursor: 'pointer',
                        fontWeight: active ? 600 : 400,
                        bgcolor: active ? 'primary.light' : 'background.paper',
                        borderColor: active ? 'primary.main' : 'grey.500',
                        color: active ? 'primary.main' : 'text.primary',
                      }}
                    >
                      {t(labelKey)}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>

          <Button variant="contained" color="primary" onClick={onRunAgain}>
            {t('matching.results.changeIps')}
          </Button>
        </Box>

        {gcs.length ? (
          gcs.map(gc => (
            <GcSection
              key={gc.id}
              gc={gc}
              sentPairKeys={sentPairKeys}
              onEmailPair={ipId => setActivePair({ applicationId: gc.id, questionnaireId: ipId })}
            />
          ))
        ) : (
          <Typography variant="body1" color="text.secondary">
            {t('matching.results.emptyPool')}
          </Typography>
        )}
      </Box>

      {activePair && (
        <Suspense fallback={null}>
          <ComposeMatchEmailModal
            applicationId={activePair.applicationId}
            questionnaireId={activePair.questionnaireId}
            runId={runId}
            onClose={() => setActivePair(null)}
            onSent={() => markPairSent(buildPairKey(activePair.applicationId, activePair.questionnaireId))}
          />
        </Suspense>
      )}
    </Box>
  );
};
